import React, { useState, useEffect, useContext } from 'react'
import { Modal, Button, Spin, Tag, Space, Tooltip } from 'antd'
import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  CloseOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { Document, Page, pdfjs } from 'react-pdf'
import { AppContext } from 'AppContext'
import './styles.css'

try {
  pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`
} catch (e) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`
}

function isImage(url) {
  if (!url || typeof url !== 'string') return false
  const cleanUrl = url.split('?')[0].split('#')[0]
  return /\.(jpg|jpeg|png|webp|svg|gif|bmp)$/i.test(cleanUrl)
}

export default function DocumentViewerModal({
  visible,
  fileUrl,
  files,
  title = 'Document Preview',
  onClose,
  canDownload = false,
  watermark = '',
}) {
  const {
    state: { isMobile },
  } = useContext(AppContext) || { state: {} }

  const fileList = React.useMemo(() => {
    if (Array.isArray(fileUrl)) return fileUrl.filter(Boolean)
    if (Array.isArray(files)) return files.filter(Boolean)
    if (fileUrl) return [fileUrl]
    return []
  }, [fileUrl, files])

  const [activeFileIndex, setActiveFileIndex] = useState(0)
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1.0)

  const activeUrl = fileList[activeFileIndex] || ''
  const isImageFile = isImage(activeUrl)

  useEffect(() => {
    if (visible) {
      setActiveFileIndex(0)
      setPageNumber(1)
      setScale(1.0)
    }
  }, [visible, fileUrl, files])

  useEffect(() => {
    if (!visible) return

    // Prevent print, save, copy shortcuts when download is restricted
    const handleKeyDown = (e) => {
      if (!canDownload) {
        if (
          (e.ctrlKey && ['p', 's', 'c', 'u'].includes(e.key.toLowerCase())) ||
          e.key === 'F12' ||
          (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase()))
        ) {
          e.preventDefault()
          e.stopPropagation()
          return false
        }
      }
    }

    const handleContextMenu = (e) => {
      if (!canDownload) {
        e.preventDefault()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('contextmenu', handleContextMenu)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('contextmenu', handleContextMenu)
    }
  }, [visible, canDownload])

  const onDocumentLoadSuccess = ({ numPages: total }) => {
    setNumPages(total)
    setPageNumber(1)
  }

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, 2.5))
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5))
  const handleZoomReset = () => setScale(1.0)

  const defaultWidth = isMobile ? 340 : 900
  const pageWidth = Math.round(defaultWidth * scale)

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '95%' : 1050}
      style={{ maxWidth: '92vw', top: 24 }}
      destroyOnClose
      centered
      closable={false}
      className='doc-viewer-modal'
      bodyStyle={{ padding: 0 }}
    >
      <div className='doc-viewer-header'>
        <div className='doc-viewer-title-box'>
          <span className='doc-viewer-filename' title={title}>
            {title}
          </span>
          {fileList.length > 1 && (
            <Tag color='purple'>
              File {activeFileIndex + 1} of {fileList.length}
            </Tag>
          )}
          {canDownload ? (
            <Tag color='green'>Verified Access</Tag>
          ) : (
            <Tag color='blue'>Protected View (Read-Only)</Tag>
          )}
        </div>
        <div className='doc-viewer-actions'>
          <Space size='small'>
            {fileList.length > 1 && (
              <>
                <Button
                  size='small'
                  icon={<LeftOutlined />}
                  disabled={activeFileIndex <= 0}
                  onClick={() => {
                    setActiveFileIndex((prev) => Math.max(prev - 1, 0))
                    setPageNumber(1)
                  }}
                >
                  Prev File
                </Button>
                <Button
                  size='small'
                  disabled={activeFileIndex >= fileList.length - 1}
                  onClick={() => {
                    setActiveFileIndex((prev) => Math.min(prev + 1, fileList.length - 1))
                    setPageNumber(1)
                  }}
                >
                  Next File <RightOutlined />
                </Button>
              </>
            )}
            <Tooltip title='Zoom Out'>
              <Button
                size='small'
                icon={<ZoomOutOutlined />}
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
              />
            </Tooltip>
            <Tooltip title='Reset Zoom'>
              <Button
                size='small'
                icon={<ReloadOutlined />}
                onClick={handleZoomReset}
              >
                {Math.round(scale * 100)}%
              </Button>
            </Tooltip>
            <Tooltip title='Zoom In'>
              <Button
                size='small'
                icon={<ZoomInOutlined />}
                onClick={handleZoomIn}
                disabled={scale >= 2.5}
              />
            </Tooltip>
            {canDownload && activeUrl && (
              <Tooltip title='Download Document'>
                <Button
                  size='small'
                  type='primary'
                  icon={<DownloadOutlined />}
                  onClick={() => window.open(activeUrl, '_blank')}
                >
                  Download
                </Button>
              </Tooltip>
            )}
            <Button
              size='small'
              icon={<CloseOutlined />}
              onClick={onClose}
            />
          </Space>
        </div>
      </div>

      <div
        className='doc-viewer-body'
        onContextMenu={(e) => !canDownload && e.preventDefault()}
      >
        {activeUrl ? (
          <div className='doc-viewer-canvas-wrapper'>
            {watermark && (
              <div className='doc-viewer-watermark'>{watermark}</div>
            )}
            {isImageFile ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 20,
                  minHeight: 450,
                  maxHeight: '80vh',
                  overflow: 'auto',
                  background: '#525659',
                }}
              >
                <img
                  src={activeUrl}
                  alt='Preview'
                  style={{
                    maxWidth: '100%',
                    maxHeight: '76vh',
                    transform: `scale(${scale})`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.2s ease',
                    pointerEvents: canDownload ? 'auto' : 'none',
                    userSelect: 'none',
                  }}
                />
              </div>
            ) : (
              <Document
                file={activeUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div style={{ padding: '80px 40px', textAlign: 'center', background: '#525659' }}>
                    <Spin size='large' tip='Loading document...' />
                  </div>
                }
                error={
                  <div style={{ color: '#ffffff', padding: '30px', textAlign: 'center' }}>
                    Unable to load PDF document.
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  width={pageWidth}
                  loading={
                    <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                      <Spin size='large' tip='Rendering page...' />
                    </div>
                  }
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            )}
          </div>
        ) : (
          <div style={{ color: '#ffffff', padding: '30px', textAlign: 'center' }}>
            No document URL specified.
          </div>
        )}
      </div>

      {!isImageFile && numPages && numPages > 0 && (
        <div className='doc-viewer-footer'>
          <Button
            size='small'
            icon={<LeftOutlined />}
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
          >
            Previous
          </Button>
          <span className='doc-viewer-page-info'>
            Page {pageNumber} of {numPages}
          </span>
          <Button
            size='small'
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber((prev) => Math.min(prev + 1, numPages))}
          >
            Next <RightOutlined />
          </Button>
        </div>
      )}
    </Modal>
  )
}
